import AuthStore from 'auth/AuthStore'
import Loader from 'reusable/mobx-helpers/Loader'
import { confirmSignIn, ConfirmSignInOutput, signIn, SignInOutput } from 'aws-amplify/auth'
import { FormHandlerConfig, formHandler } from 'reusable/mobx-store-utils/form-handlers/new/form-handler'
import { makeAutoObservable } from 'mobx'
import { snackbarError } from 'reusable/mui/snackbar/snackbar'
import { authCache } from 'cache/authCache'
import RegionRouterStore from 'features/main/RegionRouterStore'
import { ValidLoginSchema } from '@gap/backend/util/validations/validation-login'

export default class AuthSignInStore {
   loader = new Loader()
   mfaMethod: 'email' | 'totp' = 'email'
   sp

   constructor(sp: { authStore: AuthStore, regionRouterStore: RegionRouterStore }) {
      this.sp = sp
      makeAutoObservable(this)
   }

   get formHandler() {
      return formHandler(this.sp.authStore.fields, {
         email: { type: 'string' },
         password: { type: 'string' },
         rememberDevice: { type: 'boolean' }
      } satisfies FormHandlerConfig)
   }

   signIn = async () => {
      await this.loader.executeLoading(async () => {
         if (this.formHandler.validate(ValidLoginSchema)) {
            try {
               const response = await signIn({
                  username: this.sp.authStore.fields.email,
                  password: this.sp.authStore.fields.password
               })

               await this.processSignInResponse(response)
            } catch (error: unknown) {
               const isError = error instanceof Error

               if (!isError) {
                  snackbarError(`${error}`)
                  return
               }

               switch (error.name) {
                  case 'NotAuthorizedException':
                     snackbarError('Incorrect email or password. Please try again.')
                     break
                  case 'PasswordResetRequiredException':
                     this.sp.authStore.status = 'new password required'
                     break
                  default:
                     snackbarError(`Error signing in. ${error}`)
               }
            }
         }
      })
   }

   processSignInResponse = async (response: SignInOutput | ConfirmSignInOutput) => {
      switch (response.nextStep.signInStep) {
         // This state gets reached when the user has no preffered MFA but MFA is required. 
         // This used to happen when disabling MFA, but has been updated to select email as default MFA if MFA gets disabled
         case 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION': {
            this.mfaMethod = 'email'
            this.sp.authStore.status = 'verify with mfa code'
            const signInResult = await confirmSignIn({
               challengeResponse: 'EMAIL'
            })
            await this.processSignInResponse(signInResult)
            break
         }
         // When the user does not have MFA setup, do MFA via email
         case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE':
            this.mfaMethod = 'email'
            this.sp.authStore.status = 'verify with mfa code'
            break
         // When the user does have MFA setup
         case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
            this.mfaMethod = 'totp'
            this.sp.authStore.status = 'verify with mfa code'
            break
         case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
            this.sp.authStore.status = 'new password required'
            break
         case 'DONE':
            this.sp.authStore.afterSucessFullVerification?.()
            if (this.sp.authStore.pendingTrialRedirect) {
               this.sp.regionRouterStore.navigate('/trial/org-setup')
            }
            break
      }
   }

   signInWithXero = async () => {
      authCache.signedInWithXero.set(true)
      try {
         // Only provide a returnPath if necessary
         await this.sp.authStore.signInWithXero(this.sp.regionRouterStore.pathWithoutRegion !== '/' ? {
            returnPath: window.location.pathname + window.location.search,
         } : {})
      } catch (error: unknown) {
         const isError = error instanceof Error
         if (!isError) {
            snackbarError(`${error}`)
            return
         }
         snackbarError(`Error signing in. ${error}`)
      }
   }

   navigateToForgotPassword = async () => {
      this.sp.authStore.navigateToForgotPassword()
   }

   goToTrialSignUp = () => {
      this.sp.regionRouterStore.navigate('/trial')
   }
}
